import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTenant } from "@/store/tenant";
import { getIdpProvider } from "@/types";
import { Loader } from "@/components/loader";
import { Plus, Shield, Globe, Check, AlertTriangle, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/Button";

interface TenantIdPsTabProps {
  tenantId: string;
}

export const TenantIdPsTab = ({ tenantId }: TenantIdPsTabProps) => {
  const navigate = useNavigate();
  const tenantStore = useTenant(tenantId);
  const [loading, setLoading] = useState(true);

  const idps = tenantStore.idps.data;
  const availableIdps = tenantStore.idps.available;

  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          tenantStore.idps.list(),
          tenantStore.idps.listAvailable(),
        ]);
      } catch (error) {
        console.error("Failed to load IdPs:", error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white">
            Identity Providers
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Configure and manage single sign-on methods for this tenant.
          </p>
        </div>
        <Button
          onClick={() => navigate(`/m/tenants/${tenantId}/idps/add`)}
          className="bg-orange-500 hover:bg-orange-600 text-white shadow-md border-0 gap-2 px-4 rounded-lg"
        >
          <Plus className="w-4 h-4" /> Add IdP
        </Button>
      </div>

      <div className="bg-white dark:bg-slate-950 border border-slate-100 dark:border-slate-900 rounded-2xl overflow-hidden">
        {idps.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest border-b border-slate-100 dark:border-slate-900">
                  <th className="px-6 py-3">Identity Provider</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100/60 dark:divide-slate-800/60">
                {idps.map((idpConfig, idx) => {
                  const fullIdp = availableIdps.find(
                    (i) => i.id === idpConfig.idp_id,
                  );
                  const logo =
                    fullIdp?.logo_url ||
                    getIdpProvider(fullIdp?.provider || "")?.logoUrl;
                  const isConfigured =
                    idpConfig.client_id &&
                    (idpConfig.client_secret_ref || idpConfig.client_secret_masked) &&
                    idpConfig.redirect_uri &&
                    idpConfig.base_url;
                  return (
                    <tr
                      key={idx}
                      className="hover:bg-slate-50/50 dark:hover:bg-slate-900/50 transition-all cursor-pointer"
                      onClick={() =>
                        navigate(`/m/tenants/${tenantId}/idps/${idpConfig.idp_id}`)
                      }
                    >
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          {logo ? (
                            <div className="w-10 h-10 border border-slate-200/60 shadow bg-white rounded-lg flex items-center justify-center overflow-hidden shrink-0">
                              <img
                                src={logo}
                                alt={fullIdp?.display_name || ""}
                                className="w-6 h-6 object-contain"
                              />
                            </div>
                          ) : (
                            <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                              <Shield className="w-6 h-6 text-white" />
                            </div>
                          )}
                          <div>
                            <p className="text-[13px] font-bold text-slate-900 dark:text-white">
                              {fullIdp ? fullIdp.display_name : idpConfig.idp_id}
                            </p>
                            <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mt-0.5">
                              {fullIdp ? fullIdp.provider : "Identity Provider"}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-3">
                        {isConfigured ? (
                          <span className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400 flex items-center gap-1.5 w-max">
                            <Check className="w-3 h-3" />
                            Configured
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400 flex items-center gap-1.5 w-max">
                            <AlertTriangle className="w-3 h-3" />
                            Pending Setup
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right">
                        <ChevronRight className="w-4 h-4 text-slate-400 inline" />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="py-12 text-center">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe className="w-8 h-8 text-slate-400" />
            </div>
            <p className="text-slate-500 dark:text-slate-400">
              No identity providers configured
            </p>
            <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">
              Configure identity providers to enable SSO authentication
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
